import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Eraser, PenTool, Keyboard } from "lucide-react";

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 768px), (pointer: coarse)").matches;
}

export default function SignaturePad({ onChange, signerName }) {
  const [mode, setMode] = useState(isMobileViewport() ? "drawn" : "typed");
  const [typedName, setTypedName] = useState(signerName || "");
  const [attested, setAttested] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  useEffect(() => {
    if (mode !== "drawn") return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
  }, [mode]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0];
    const x = (t ? t.clientX : e.clientX) - rect.left;
    const y = (t ? t.clientY : e.clientY) - rect.top;
    return { x, y };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    hasDrawnRef.current = true;
  };
  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    if (hasDrawnRef.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      onChange({ signature_type: "drawn", signature_data: dataUrl });
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawnRef.current = false;
    onChange({ signature_type: "drawn", signature_data: "" });
  };

  const handleTypedChange = (name, isAttested) => {
    setTypedName(name);
    setAttested(isAttested);
    if (name.trim() && isAttested) {
      onChange({ signature_type: "typed", signature_data: name.trim() });
    } else {
      onChange({ signature_type: "typed", signature_data: "" });
    }
  };

  return (
    <div className="space-y-3" data-testid="signature-pad">
      <div className="flex gap-2">
        <Button
          type="button"
          variant={mode === "drawn" ? "default" : "outline"}
          onClick={() => setMode("drawn")}
          className="tap-target flex-1"
          data-testid="sig-mode-drawn"
        >
          <PenTool className="w-4 h-4 mr-2" /> Draw
        </Button>
        <Button
          type="button"
          variant={mode === "typed" ? "default" : "outline"}
          onClick={() => setMode("typed")}
          className="tap-target flex-1"
          data-testid="sig-mode-typed"
        >
          <Keyboard className="w-4 h-4 mr-2" /> Type
        </Button>
      </div>

      {mode === "drawn" ? (
        <div>
          <canvas
            ref={canvasRef}
            className="signature-canvas"
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            data-testid="signature-canvas"
          />
          <div className="flex justify-between items-center mt-2">
            <p className="text-xs text-muted-foreground">Sign above with finger or mouse</p>
            <Button type="button" variant="ghost" size="sm" onClick={clear} data-testid="sig-clear">
              <Eraser className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label htmlFor="typed-sig" className="text-label">Type Full Legal Name</Label>
            <Input
              id="typed-sig"
              value={typedName}
              onChange={(e) => handleTypedChange(e.target.value, attested)}
              placeholder="e.g. Sarah Williams"
              className="text-lg h-12 mt-1 font-display"
              data-testid="typed-signature-input"
            />
          </div>
          <label className="flex items-start gap-3 cursor-pointer p-3 rounded-md bg-secondary border border-border">
            <Checkbox
              checked={attested}
              onCheckedChange={(v) => handleTypedChange(typedName, !!v)}
              className="mt-0.5 h-5 w-5"
              data-testid="typed-signature-attest"
            />
            <span className="text-sm leading-snug">
              I attest that the information above is true and accurate, and this typed name
              constitutes my legal electronic signature.
            </span>
          </label>
        </div>
      )}
    </div>
  );
}
